import { EMPTY_VALUE } from "staticConfiguration";
import type { TFunction } from "core/entities";
import { useCentrixApiAnonymous } from "core/hooks/useCentrixApiAnonymous";
import { getBrand } from "core/utils/getBrand";
import {
  EstimateRequestDisplay,
  type EstimateRequestDisplayProps,
} from "features/EstimateRequest/components/EstimateRequestDisplay";
import { ShopInfoDisplay } from "features/EstimateRequest/components/ShopInfoDisplay";
import { useEstimateRequestEnumTranslations } from "features/EstimateRequest/hooks/useEstimateRequestEnumTranslations";
import { useInsurerSelectOptions } from "features/PaymentRequest/hooks";
import { useRouter } from "next/router";
import { useMemo } from "react";
import type { usePublicEstimateRequestCustomerInformationFormMethods } from "./PublicEstimateRequestCustomerInformationForm";
import type { usePublicEstimateRequestDamageInformationFormMethods } from "./PublicEstimateRequestDamageInformationForm";
import type { usePublicEstimateRequestVehicleInformationFormFormMethods } from "./PublicEstimateRequestVehicleInformationForm";

type PublicEstimateRequestConfirmAndSubmitProps = {
  t: TFunction;
  vehicleInformationFormMethods: ReturnType<
    typeof usePublicEstimateRequestVehicleInformationFormFormMethods
  >;
  customerInformationFormMethods: ReturnType<
    typeof usePublicEstimateRequestCustomerInformationFormMethods
  >;
  damageInformationFormMethods: ReturnType<
    typeof usePublicEstimateRequestDamageInformationFormMethods
  >;
  errorSlot?: React.ReactNode;
};
export function PublicEstimateRequestConfirmAndSubmit({
  customerInformationFormMethods,
  damageInformationFormMethods,
  vehicleInformationFormMethods,
  errorSlot,
  t,
}: PublicEstimateRequestConfirmAndSubmitProps) {
  const {
    vehicleModel,
    vehicleYear,
    vehicleMake,
    vinNumber,
    vinImage,
    paymentType,
    insurerId,
  } = vehicleInformationFormMethods.watch();
  const { email, firstName, lastName, notes, phoneNumber, shopId } =
    customerInformationFormMethods.watch();
  const { damageLocations, otherImages } = damageInformationFormMethods.watch();

  const {
    locale,
    query: { region },
  } = useRouter();

  const CountryIso2AlphaCodes =
    typeof region === "string" ? [region] : undefined;
  const { brandCode } = getBrand();
  const { data: shopData } = useCentrixApiAnonymous({
    path: "/api/shared-entities/shops/anonymous-public",
    parameters: {
      query: {
        UserLanguageCode: locale,
        ShopStatus: "Active",
        BrandShortCodes: [brandCode],
        CountryIso2AlphaCodes,
      },
    },
    fetchAll: true,
  });
  const enumTranslations = useEstimateRequestEnumTranslations();
  const { insurersOptions } = useInsurerSelectOptions();

  const paymentMethod =
    paymentType === "InsurerPay"
      ? enumTranslations.EstimateRequestPaymentMethod.InsurerPay
      : enumTranslations.EstimateRequestPaymentMethod.CustomerPay;
  const insurerName =
    insurersOptions.find((opt) => opt.value === insurerId)?.label ??
    EMPTY_VALUE;

  const damages: EstimateRequestDisplayProps["damages"] = useMemo(() => {
    const processedLocations = damageLocations.map(({ location, images }) => ({
      location,
      images: images.map(({ file }) => ({
        src: file ? URL.createObjectURL(file) : "",
        alt: t("damageImageAltText", {
          location: enumTranslations.EstimateImageLocation[location],
        }),
      })),
    }));
    const others = otherImages.reduce(
      (acc, { file, fileId }) => {
        if (!file || !fileId) {
          return acc;
        }
        acc.images.push({
          src: file ? URL.createObjectURL(file) : "",
          alt: t("damageImageAltText", {
            location: enumTranslations.EstimateImageLocation.Other,
          }),
        });
        return acc;
      },
      {
        location: "Other",
        images: [],
      } as EstimateRequestDisplayProps["damages"][number]
    );
    processedLocations.push(others);
    return processedLocations;
  }, [damageLocations, otherImages, enumTranslations.EstimateImageLocation, t]);

  const selectedShop = shopData?.items?.find((shop) => shop.id === shopId);
  const vinImageSrc = useMemo(
    () => (vinImage ? URL.createObjectURL(vinImage) : ""),
    [vinImage]
  );
  return (
    <>
      <EstimateRequestDisplay
        vehicleMake={vehicleMake}
        vehicleModel={vehicleModel}
        vehicleYear={vehicleYear?.getFullYear() ?? 0}
        vinNumber={vinNumber}
        vinImageSrc={vinImageSrc}
        firstName={firstName}
        lastName={lastName}
        notes={notes}
        phoneNumber={phoneNumber}
        shop={
          selectedShop ? (
            <ShopInfoDisplay shopInfo={selectedShop} />
          ) : (
            EMPTY_VALUE
          )
        }
        damages={damages}
        email={email}
        paymentMethod={paymentMethod}
        insurerName={insurerName}
      />
      {errorSlot}
    </>
  );
}
